<template>
  <div v-if="!readonly">
    <el-form ref="formRef" :model="order" label-position="top" require-asterisk-position="right">
      <!-- Selects -->
      <div class="col">
        <div class="row">
          <el-form-item prop="bodySide" :label="$t('views.customize.workflow.form.bodySide')">
            <el-select id="body-side" v-model="order.bodySide" :placeholder="$t('views.configurator.pleaseSelect')">
              <el-option v-for="side in Object.values(BodySide)" :key="side" :label="$t(`enum.bodySide.${side}`)" :value="side" />
            </el-select>
          </el-form-item>
        </div>
      </div>
      <div class="row">
        <el-form-item prop="automatic-fit">
          <div class="col">
            <label for="automatic-fit" class="el-form-item__label mb-0">{{ $t('views.customize.workflow.form.automaticFit') }}</label>
            <el-switch id="automatic-fit" v-model="automaticFit" :disabled="product.type.name.toLowerCase() == 'covers'" @change="automaticFitChanged()" />
          </div>
        </el-form-item>
      </div>
      <!-- Progress bars -->
      <div v-if="automaticFit">
        <div class="row">
          <el-form-item prop="width-range">
            <div class="col">
              <label for="width-range" class="el-form-item__label mb-0">{{ $t('components.order-form.height') }}</label>
              <el-slider id="width-range" v-model="order.heightWithoutDeduction" :min="100" :max="460" />
              <el-input-number v-model="order.heightWithoutDeduction" />
            </div>
          </el-form-item>
        </div>
        <div class="row">
          <el-form-item prop="scope-range">
            <div class="col">
              <label for="scope-range" class="el-form-item__label mb-0">{{ $t('components.order-form.length') }}</label>
              <el-slider id="scope-range" v-model="order.depth" :min="100" :max="300" />
              <el-input-number v-model="order.depth" />
            </div>
          </el-form-item>
        </div>
        <div class="row">
          <div v-loading="bodyAttributes.length <= 0 && authentication.isAuthenticated" class="col mb-3" element-loading-background="rgba(122, 122, 122, 0)">
            <el-form-item prop="scan">
              <label for="scan" class="el-form-item__label">{{ $t('views.customize.workflow.form.scanFiles') }}</label>
              <select id="scan" v-model="selectedBodyAttribute" class="form-select" @change="$emit('scanFileChanged', selectedBodyAttribute)">
                <option :value="null" :label="$t(`components.product-parameter.scan.none`)" />
                <option
                  v-for="bodyAttribute in bodyAttributes"
                  :key="bodyAttribute.characteristicId"
                  :value="bodyAttribute"
                  :label="$t(`components.product-parameter.scan.${bodyAttribute.bodyPart?.name}`) + ' - ' + bodyAttribute.scanFile?.name"
                />
              </select>
            </el-form-item>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="row mt-1">
          <el-form-item prop="depth">
            <div class="col">
              <label for="width-range" class="el-form-item__label mb-0">{{ $t('components.order-form.scope-top') }}</label>
              <el-slider id="width-range" v-model="order.depth" :min="150" :max="450" :step="1" />
              <el-input-number v-model="order.depth" />
            </div>
          </el-form-item>
        </div>
        <div class="row mt-1">
          <el-form-item prop="heightWithoutDeduction">
            <div class="col">
              <label for="height-range" class="el-form-item__label mb-0">{{ $t('components.order-form.height') }}</label>
              <el-slider id="height-range" v-model="order.heightWithoutDeduction" :min="200" :max="460" />
              <el-input-number v-model="order.heightWithoutDeduction" />
            </div>
          </el-form-item>
        </div>
        <div class="row mt-1">
          <el-form-item prop="calfCircumferenceWithoutDeduction">
            <div class="col">
              <label for="scope-range" class="el-form-item__label mb-0">{{ $t('components.order-form.scope-bottom') }}</label>
              <el-slider id="scope-range" v-model="order.calfCircumferenceWithoutDeduction" :min="100" :max="300" />
              <el-input-number v-model="order.calfCircumferenceWithoutDeduction" />
            </div>
          </el-form-item>
        </div>
      </div>
    </el-form>
  </div>
  <div v-else>
    <div class="row1">
      <label class="col-sm-6 form-label text-secondary mb-1" for="body-side-readonly">{{ $t('views.customize.workflow.form.bodySide') }}</label>
      <label class="col-sm-6 form-label mb-1">{{ order?.bodySide }}</label>
    </div>
    <div class="row1">
      <label class="col-sm-6 form-label text-secondary mb-1">{{ $t('views.customize.workflow.form.kneeJoint') }}</label>
      <label class="col-sm-6 form-label mb-1">{{ order?.kneeJoint }}</label>
    </div>
    <div class="row1">
      <label class="col-sm-6 form-label text-secondary mb-1">{{ $t('views.customize.workflow.form.foot') }}</label>
      <label class="col-sm-6 form-label mb-1">{{ order?.foot }}</label>
    </div>
    <div v-if="product?.name?.toLowerCase().includes('u-flex') || product?.name?.toLowerCase().includes('c-flex')">
      <div class="row1">
        <label class="col-sm-6 form-label text-secondary mb-1">{{ $t('views.customize.workflow.form.zip') }}</label>
        <label class="col-sm-6 form-label mb-1">{{ order?.zip }}</label>
      </div>
    </div>
    <div v-if="!automaticFit">
      <div class="row1">
        <label class="col-sm-6 form-label text-secondary mb-1">{{ $t('components.order-form.scope-top') }}</label>
        <label class="col-sm-6 form-label mb-1">{{ order?.depth }}</label>
      </div>
      <div class="row1">
        <label class="col-sm-6 form-label text-secondary mb-1">{{ $t('components.order-form.height') }}</label>
        <label class="col-sm-6 form-label mb-1">{{ order?.heightWithoutDeduction }}</label>
      </div>
      <div class="row1">
        <label class="col-sm-6 form-label text-secondary mb-1">{{ $t('components.order-form.scope-bottom') }}</label>
        <label class="col-sm-6 form-label mb-1">{{ order?.calfCircumferenceWithoutDeduction }}</label>
      </div>
    </div>
    <div v-else>
      <div class="row1">
        <label class="col-sm-6 form-label text-secondary mb-1">{{ $t('views.customize.workflow.form.scanFiles') }}</label>
        <label class="col-sm-6 form-label mb-1">{{ selectedBodyAttribute }}</label>
      </div>
      <div class="row1">
        <label class="col-sm-6 form-label text-secondary mb-1">{{ $t('components.order-summary.bilateralAmputee') }}</label>
        <label class="col-sm-6 form-label mb-1">{{ $t(`enum.bilateralAmputeeType.${order?.bilateralAmputee}`) }}</label>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-facing-decorator';

import { BodySide, ZipType } from '@/types/enum/order';
import * as CustomerService from '@/services/api/customerService';
import Auth from '@/services/api/auth';
import { Body_Attribute } from '@/types/api/Patient/BodyAttribute';
import { Product } from '@/types/api/Model/Product';
import { OrderRequestData } from '@/services/api/orderService';
import { useUserStore } from '@/stores/userStore';

@Component({
  components: {},
  emits: ['scanFileChanged', 'zipChanged'],
})
@Component
export default class ProductParamterCover extends Vue {
  @Prop() order!: OrderRequestData;
  @Prop() product!: Product;
  @Prop({ default: false }) readonly!: boolean;

  authentication = Auth.getInstance();
  bodyAttributes: Body_Attribute[] = [];
  userStore = useUserStore();

  BodySide = BodySide;
  ZipType = ZipType;
  selectedBodyAttribute: Body_Attribute | null = null;

  automaticFit = false;

  get isLuttermannDomain() {
    return this.userStore.domain?.urlSuffix === 'luttermann';
  }

  mounted(): void {
    /*this.order.heightWithoutDeduction = 420;
    this.order.depth = 180;*/

    if (this.authentication.user) {
      this.automaticFit = true;
      CustomerService.getBodyAttributesByUserId(this.authentication.user.userId).then((bodyAttributes) => {
        this.bodyAttributes = bodyAttributes;
      });
    }
  }

  automaticFitChanged(): void {
    if (!this.automaticFit) this.selectedBodyAttribute = null;
  }
}
</script>

<style scoped lang="scss">
.el-form-item {
  margin-top: 0.5rem;
  font-weight: normal;
  color: #1d1e19;
}
:deep(.el-form-item__label) {
  color: #1d1e19;
}
.text-secondary {
  color: #0000008d !important;
}
.form-label {
  margin-top: 0.5rem;
  font-weight: normal;
  color: #1d1e19;
}
.row1 {
  border-bottom: 0.1rem solid #6767678d;
}

:deep(.custom-switch .el-switch__label) {
  color: white !important;
}

.el-slider {
  margin: 0.2rem;
  width: auto;
}

.el-input-number {
  width: 100%;
}
</style>
